import * as React from 'react';

import { QueryHookOptions, useQuery } from '@apollo/client';

import {
  GetReviewsQuery,
  GetReviewsQueryVariables,
} from '@notino/shared/definitions/types';

import { useReviewFiltersStore } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/SortFilterBar/Stores/filtersStore';
import { useReviewSortingStore } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/SortFilterBar/Stores/sortingStore';

import getReviewsQuery from './queries/reviews.graphql';

export const hasImageReviewsFromData = (
  data: GetReviewsQuery | undefined
): boolean =>
  (data?.reviewsAndFilters?.filter?.contentTypes?.find(
    (c) => c?.type === 'WithImages'
  )?.count ?? 0) > 0;

export const useReviewsFetch = (
  masterProductId: string,
  options: QueryHookOptions<GetReviewsQuery, GetReviewsQueryVariables>
) => {
  const { orderBy, orderDesc, page, hideTranslated } = useReviewSortingStore();

  const allSelections = useReviewFiltersStore((state) => state.selections);

  const variables: GetReviewsQueryVariables = React.useMemo(
    () => ({
      masterProductId,
      orderBy,
      orderDesc,
      page,
      hideTranslated,
      rating: allSelections.rating as number[],
      contentTypes: ['WithText', ...(allSelections.contentTypes as string[])],
      variantIds: allSelections.variants as number[],
      shopIds: allSelections.shops as string[],
    }),
    [masterProductId, orderBy, orderDesc, page, hideTranslated, allSelections]
  );

  const result = useQuery<GetReviewsQuery, GetReviewsQueryVariables>(
    getReviewsQuery,
    {
      variables,
      partialRefetch: true,
      ...options,
    }
  );

  return React.useMemo(() => {
    const data = result.data;
    if (!data?.reviewsAndFilters?.filter?.contentTypes) {
      return result;
    }
    return {
      ...result,
      data: {
        ...data,
        reviewsAndFilters: {
          ...data.reviewsAndFilters,
          filter: {
            ...data.reviewsAndFilters.filter,
            contentTypes: data.reviewsAndFilters.filter.contentTypes.filter(
              (ct) => ct?.type !== 'WithText'
            ),
          },
        },
      },
    };
  }, [result]);
};
